import { TransactionView } from "../../../models/view/transaction/transaction.view";
import { PluginTransaction } from "../../../shared/utils/helper.plugin/plugin.transactions.map";

/** Format a Date as `YYYY-MM-DD` in local calendar components. */
export function formatDateYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Today's date in `YYYY-MM-DD`. */
export function getSyncEndDateToday(): string {
  return formatDateYmd(new Date());
}

/** One year before today in `YYYY-MM-DD` (first sync / empty vault fallback). */
export function getDefaultSyncStartDateOneYearAgo(): string {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 1);
  return formatDateYmd(date);
}

function effectiveTransactionDate(tx: TransactionView): Date | undefined {
  return tx.postDate ?? tx.transactionDate;
}

/**
 * Incremental sync window start: latest vault transaction calendar date for the account,
 * or one year ago when the vault has no transactions.
 */
export function getIncrementalSyncStartDate(
  vaultTransactions: TransactionView[],
  accountId: string,
): string {
  const accountTransactions = vaultTransactions.filter(
    (t) => t.accountLink?.id === accountId && t.definition === "transaction",
  );
  if (accountTransactions.length === 0) {
    return getDefaultSyncStartDateOneYearAgo();
  }
  const sorted = [...accountTransactions].sort((a, b) => {
    const da = effectiveTransactionDate(a)?.getTime() ?? 0;
    const db = effectiveTransactionDate(b)?.getTime() ?? 0;
    return db - da;
  });
  const latestDate = effectiveTransactionDate(sorted[0]);
  if (!latestDate) {
    return getDefaultSyncStartDateOneYearAgo();
  }
  return formatDateYmd(latestDate);
}

/** Vault transaction ids for an account (stable plugin ids from S4). */
export function collectVaultTransactionIdsForAccount(
  vaultTransactions: TransactionView[],
  accountId: string,
): Set<string> {
  // As strings: an id a plugin once sent as a number matches the same id sent as a string.
  const ids = vaultTransactions
    .filter((t) => t.accountLink?.id === accountId)
    .map((t) => (t.id == null ? "" : String(t.id)))
    .filter(Boolean);
  return new Set(ids);
}

/** Drop mapped rows whose stable id already exists in the vault (same-day overlap). */
export function dedupeTransactionsByVaultIds(
  transactions: TransactionView[],
  existingIds: Set<string>,
  logService?: { warning: (msg: string) => void },
): TransactionView[] {
  return transactions.filter((tx) => {
    const id = tx.id ?? "";
    if (id.length === 0) {
      logService?.warning("dedupeTransactionsByVaultIds: dropping transaction with empty id");
      return false;
    }
    return !existingIds.has(id);
  });
}

/**
 * Calendar `YYYY-MM-DD` from plugin `transactionTime` (ISO or legacy `MM-DD-YYYY HH:mm`).
 */
export function pluginTransactionCalendarYmd(transactionTime: string): string | null {
  const raw = (transactionTime || "").trim();
  if (!raw) {
    return null;
  }
  if (raw.includes("T")) {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) {
      return null;
    }
    return formatDateYmd(d);
  }
  const [datePart] = raw.split(" ");
  if (!datePart) {
    return null;
  }
  const [month, day, year] = datePart.split("-");
  if (!month || !day || !year) {
    return null;
  }
  const y = parseInt(year, 10);
  const m = parseInt(month, 10);
  const d = parseInt(day, 10);
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) {
    return null;
  }
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** True when the plugin row's calendar date is on or after `startDateYmd` (inclusive). */
export function isPluginTransactionOnOrAfterStartDate(
  transactionTime: string,
  startDateYmd: string,
): boolean {
  const ymd = pluginTransactionCalendarYmd(transactionTime);
  if (!ymd) {
    return true;
  }
  return ymd >= startDateYmd;
}

/** Client-side guard when the server returns a full cached scrape. */
export function filterPluginTransactionsFromStartDate(
  transactions: PluginTransaction[],
  startDateYmd: string,
): PluginTransaction[] {
  return transactions.filter((tx) =>
    isPluginTransactionOnOrAfterStartDate(tx.transactionTime, startDateYmd),
  );
}
